/* ═══════════════════════════════════════════════════════════════
   Flaschenformen und Drehkörper

   Eine Weinflasche ist — bis auf den Bocksbeutel — ein Drehkörper:
   Ihre ganze Gestalt steckt in EINER Umrisslinie, dem Radius über
   der Höhe. Darauf beruht das Rundum-Etikett:

   - Die Aufnahme (kern/abwicklung.js) braucht die Umrisslinie, um
     zu wissen, wie breit die Flasche auf welcher Höhe im Bild ist.
     Der Hals dreht sich mit demselben Winkel wie der Bauch, legt
     dabei aber viel weniger Weg zurück.
   - Die 3D-Ansicht (dienste/flasche3d.js) dreht dieselbe Linie um
     die Achse und legt das abgewickelte Bild als Haut darüber.

   Statt die Form aus dem Kamerabild zu schätzen, wählt man eine von
   vier Grundformen. Das ist robuster als jede Kantenerkennung an
   dunklem, spiegelndem Glas vor beliebigem Hintergrund — und die
   Formen unterscheiden sich in der Praxis vor allem in Schulter und
   Halslänge, kaum im Bauch. Weicht die echte Flasche ein paar
   Millimeter ab, merkt man es weder in der Abwicklung noch im Modell.

   Maße in Millimetern, gemessen an üblichen 0,75-l-Flaschen; die
   Werte sind gerundete Richtgrößen, keine Norm. `profil` läuft vom
   Boden (h = 0) zur Mündung, `kapsel` ist der übliche Anteil der
   Gesamthöhe, den die Kapsel von oben bedeckt.

   Reine Logik ohne I/O; Tests in tests/flasche.test.mjs.
   ═══════════════════════════════════════════════════════════════ */

const FLASCHENFORMEN = {
  bordeaux: {
    name: "Bordeaux", hoehe: 300, kapsel: 0.16,
    profil: [[0, 34], [3, 37.5], [9, 38], [195, 38], [208, 36.5], [220, 31], [230, 23.5],
      [239, 17.5], [248, 15], [288, 14.6], [290, 16], [299, 15.8], [300, 14.5]],
  },
  burgund: {
    name: "Burgund", hoehe: 296, kapsel: 0.16,
    profil: [[0, 36], [4, 39.5], [10, 40], [140, 40], [163, 38.5], [188, 33], [212, 24.5],
      [232, 17.5], [248, 15], [284, 14.8], [286, 16.2], [295, 16], [296, 14.8]],
  },
  schlegel: {
    name: "Schlegel", hoehe: 350, kapsel: 0.13,
    profil: [[0, 33], [4, 36.8], [10, 37.2], [120, 37.2], [150, 35], [190, 27.5], [228, 19.5],
      [258, 15.2], [336, 14.2], [338, 15.4], [349, 15.2], [350, 14]],
  },
  champagner: {
    name: "Champagner", hoehe: 300, kapsel: 0.3,
    profil: [[0, 40], [5, 44], [12, 44.5], [150, 44.5], [174, 42], [204, 32.5], [226, 21.5],
      [240, 16.5], [284, 15.3], [287, 17.2], [297, 16.6], [300, 14]],
  },
};

const FORM_REIHE = ["bordeaux", "burgund", "schlegel", "champagner"];

/** Form zu einem Schlüssel — oder eine schon fertige Form (etwa der
    beim Rundum-Etikett gemessene Umriss), wenn sie brauchbar ist:
    Höhe und mindestens drei Stützpunkte [Höhe, Radius] in mm, von
    unten nach oben. Alles andere ergibt die Bordeaux-Form. */
function formVon(angabe) {
  if (angabe && typeof angabe === "object") {
    const p = angabe.profil;
    const brauchbar = angabe.hoehe > 0 && Array.isArray(p) && p.length >= 3
      && p.every((q, i) => Array.isArray(q) && Number.isFinite(q[0]) && Number.isFinite(q[1]) && q[1] >= 0 && (i === 0 || q[0] >= p[i - 1][0]));
    if (brauchbar) return { name: angabe.name || FLASCHENFORMEN.bordeaux.name, kapsel: FLASCHENFORMEN.bordeaux.kapsel, ...angabe };
    return FLASCHENFORMEN.bordeaux;
  }
  return FLASCHENFORMEN[angabe] || FLASCHENFORMEN.bordeaux;
}


/** Radius in mm auf Höhe `h` (mm über dem Boden), linear zwischen
    den Stützpunkten. Außerhalb der Flasche 0. */
function radiusBei(form, h) {
  const p = form.profil;
  if (!(h >= 0) || h > form.hoehe) return 0;
  for (let i = 1; i < p.length; i++) {
    if (h <= p[i][0]) {
      const [h0, r0] = p[i - 1], [h1, r1] = p[i];
      return h1 === h0 ? r1 : r0 + (r1 - r0) * (h - h0) / (h1 - h0);
    }
  }
  return p[p.length - 1][1];
}

const koerperRadius = (form) => Math.max(...form.profil.map((p) => p[1]));


/** Wo der Hals beginnt: die tiefste Höhe, ab der die Flasche nicht
    mehr breiter als das 1,25-Fache des Halses wird. Begrenzt die
    Suche nach der Kapselkante — tiefer reicht keine Kapsel. */
function halsBeginn(form) {
  /* Ohne den letzten Punkt: Die Mündung läuft dort nach innen und
     ist schmaler als der Hals. */
  const hals = Math.min(...form.profil.filter((p) => p[0] > form.hoehe * 0.6 && p[0] < form.hoehe).map((p) => p[1]));
  for (let h = form.hoehe; h >= 0; h -= 0.5) {
    if (radiusBei(form, h) > hals * 1.25) return h;
  }
  return 0;
}


/* Rebsorten, die man in der Regel in Burgunder- bzw. Schlegelflaschen
   findet. Nur ein Vorschlag für die Vorauswahl — gewählt wird in der
   Aufnahme, und die Wahl ist mit einem Fingertipp geändert. */
const BURGUND_REBEN = ["pinot noir", "spätburgunder", "chardonnay", "pinot blanc", "weißburgunder",
  "weissburgunder", "grauburgunder", "pinot gris", "syrah", "grenache", "nebbiolo", "viognier", "gamay"];
const SCHLEGEL_REBEN = ["riesling", "gewürztraminer", "gewurztraminer", "müller-thurgau", "kerner",
  "scheurebe", "elbling", "muskateller"];

function vorschlagForm(wein) {
  if (!wein) return "bordeaux";
  if (wein.typ === "schaum") return "champagner";
  const namen = [
    ...(Array.isArray(wein.rebsortenListe) ? wein.rebsortenListe.map((r) => r && r.name) : []),
    wein.rebsorten,
  ].filter(Boolean).join(" ").toLowerCase();
  if (SCHLEGEL_REBEN.some((r) => namen.includes(r))) return "schlegel";
  if (BURGUND_REBEN.some((r) => namen.includes(r))) return "burgund";
  return "bordeaux";
}


/* ── Kapsel ────────────────────────────────────────────────────
   Die Art leitet sich aus den Freitexten ab, die es am Jahrgang
   ohnehin gibt („Kapsel": „Zinn, Wachs, ohne", „Verschluss":
   „Naturkork, Schraubverschluss"). Damit sieht das Modell so aus,
   wie man es eingetragen hat, ohne eine weitere Frage zu stellen. */
const KAPSEL_ARTEN = ["folie", "wachs", "schraub", "ohne"];

/** Tiefste Höhe (mm), bis zu der eine Kapsel reichen kann: etwas
    unter den Halsansatz — Champagnerfolien laufen über die Schulter. */
const kapselTiefste = (form) => Math.max(0, halsBeginn(form) - 45);

function kapselArt(jg) {
  const kapsel = String((jg && jg.kapsel) || "").toLowerCase();
  const verschluss = String((jg && jg.verschluss) || "").toLowerCase();
  if (/schraub|stelvin|screw|drehverschluss/.test(verschluss) || /schraub/.test(kapsel)) return "schraub";
  if (/wachs|wax|cire|lacre/.test(kapsel)) return "wachs";
  if (/^\s*(ohne|keine|kein|none|sans|sin)\b/.test(kapsel)) return "ohne";
  return "folie";
}

/* Wie die Kapsel aufträgt (mm über dem Glas). Wachs ist dick, ein
   Schraubverschluss eine Blechhülse, Folie liegt fast auf. */
const KAPSEL_DICKE = { folie: 0.5, wachs: 1.6, schraub: 0.7, ohne: 0 };

/* Wie rund die Kapsel ist (mm): `kante` rundet die obere Kante (die
   Rundung außen ist kante + Dicke), `kehle` füllt Kehlen wie die unter
   dem Mündungswulst. Wachs ist getaucht und läuft rund über Kante und
   Wulst; Folie und Blech liegen eng an. Das Glas endet unter der
   Kapsel (siehe flaschenTeile) und kann an der gerundeten Kante nicht
   durchstoßen. */
const KAPSEL_RUNDUNG = { folie: { kante: 1.2, kehle: 1 }, schraub: { kante: 1.2, kehle: 0.5 }, wachs: { kante: 7, kehle: 4 } };


/* ── Drehkörper als Dreiecksnetz ───────────────────────────────

   `punkte`: [{ h, r }] in mm, von unten nach oben. Ergebnis ist ein
   Netz für WebGL: Positionen (x, y, z) mit y als Achse, Normalen,
   Texturkoordinaten und Indizes. u läuft einmal um die Flasche,
   v = h / hoehe — genau die Zuordnung der Abwicklung, deren oberste
   Zeile die Mündung ist.

   Die Naht bei u = 0/1 bekommt doppelte Ecken, sonst liefe die
   Textur auf dem letzten Streifen einmal rückwärts um die Flasche.

   `unterkante(winkel)` verschiebt die unterste Reihe — für die
   herablaufende Wachskante; ohne sie ist die Kante gerade. */
function drehkoerperNetz(punkte, { segmente = 72, hoehe, unterkante = null } = {}) {
  const n = punkte.length;
  const gesamt = hoehe || Math.max(...punkte.map((p) => p.h));
  const spalten = segmente + 1;
  const positionen = new Float32Array(n * spalten * 3);
  const normalen = new Float32Array(n * spalten * 3);
  const uv = new Float32Array(n * spalten * 2);

  /* Normale in der Profilebene: senkrecht zur Umrisslinie, gemittelt
     über die beiden angrenzenden Abschnitte. (dr, dh) ist die
     Tangente, (dh, -dr) zeigt nach außen. */
  const profilNormale = (i) => {
    const a = punkte[Math.max(0, i - 1)], b = punkte[Math.min(n - 1, i + 1)];
    let nr = b.h - a.h, nh = -(b.r - a.r);
    const laenge = Math.hypot(nr, nh) || 1;
    return [nr / laenge, nh / laenge];
  };

  for (let i = 0; i < n; i++) {
    const [nr, nh] = profilNormale(i);
    for (let s = 0; s < spalten; s++) {
      const u = s / segmente;
      const winkel = u * Math.PI * 2;
      let h = punkte[i].h;
      if (i === 0 && unterkante) h += unterkante(winkel);
      const r = punkte[i].r;
      const k = (i * spalten + s);
      /* Blick von vorn auf u = 0.5: Bei Winkel 0 liegt die Naht
         hinten (−z), die Mitte der Abwicklung zeigt zum Betrachter. */
      const sx = -Math.sin(winkel), cz = -Math.cos(winkel);
      positionen[k * 3] = r * sx;
      positionen[k * 3 + 1] = h;
      positionen[k * 3 + 2] = r * cz;
      normalen[k * 3] = nr * sx;
      normalen[k * 3 + 1] = nh;
      normalen[k * 3 + 2] = nr * cz;
      uv[k * 2] = u;
      uv[k * 2 + 1] = Math.min(1, h / gesamt);
    }
  }

  const indizes = new (n * spalten > 65535 ? Uint32Array : Uint16Array)((n - 1) * segmente * 6);
  let q = 0;
  for (let i = 0; i < n - 1; i++) {
    for (let s = 0; s < segmente; s++) {
      const a = i * spalten + s, b = a + 1, c = a + spalten, d = c + 1;
      indizes[q++] = a; indizes[q++] = c; indizes[q++] = b;
      indizes[q++] = b; indizes[q++] = c; indizes[q++] = d;
    }
  }
  return { positionen, normalen, uv, indizes, hoehe: gesamt };
}


/** Feine Stützpunkte der Umrisslinie zwischen zwei Höhen — gleich
    verteilt plus alle Knicke des Profils, damit Schulter und Wulst
    der Mündung nicht weichgezeichnet werden. */
function profilPunkte(form, von = 0, bis = form.hoehe, { schritt = 3, aufschlag = 0 } = {}) {
  const hoehen = new Set();
  for (let h = von; h < bis; h += schritt) hoehen.add(Math.round(h * 100) / 100);
  hoehen.add(bis);
  for (const [h] of form.profil) if (h > von && h < bis) hoehen.add(h);
  return [...hoehen].sort((a, b) => a - b).map((h) => ({ h, r: radiusBei(form, h) + aufschlag }));
}


/** Umriss der Kapsel (mm, von unten nach oben): `mantel` von `unten`
    bis dorthin, wo die gerundete Kante waagerechter als 45° wird, dann
    `deckel` über die Kante bis zur Achse.

    Rechnung im Schnitt (Höhe, Radius): Das Glas wird um `kante`
    geöffnet (alle Kreise dieses Radius, die ganz im Glas liegen) und
    um kante + Dicke wieder aufgeweitet. Konvexe Ecken — die Kante der
    Mündung, ihr Wulst — werden so mit kante + Dicke gerundet, überall
    sonst liegt die Kapsel um die Dicke auf. Danach füllt eine
    Schließung mit `kehle` die Kehlen. Beim Champagner wölbt sich der
    Deckel über den Agraffenkorken. */
function kapselUmriss(form, art, unten) {
  const H = form.hoehe;
  const dicke = KAPSEL_DICKE[art] || KAPSEL_DICKE.folie;
  const { kante: rho, kehle } = KAPSEL_RUNDUNG[art] || KAPSEL_RUNDUNG.folie;
  const Q = rho + dicke, S = 0.25;
  /* Mittelpunkte der Kreise im Glas: zu jeder Höhe der äußerste */
  const mitten = [];
  for (let hc = Math.max(rho, unten - Q - kehle); hc <= H - rho + 1e-9; hc += S) {
    let rc = Infinity;
    for (let h = hc - rho; h <= hc + rho + 1e-9; h += 0.1) rc = Math.min(rc, radiusBei(form, Math.min(H, h)) - Math.sqrt(Math.max(0, rho * rho - (h - hc) ** 2)));
    for (const [hk, rk] of form.profil) if (Math.abs(hk - hc) <= rho) rc = Math.min(rc, rk - Math.sqrt(rho * rho - (hk - hc) ** 2));
    mitten.push([hc, rc]);
  }
  if (!mitten.length) return null;
  const aussen = (h) => {
    let r = -Infinity;
    for (const [hc, rc] of mitten) { const d = h - hc; if (Math.abs(d) <= Q) r = Math.max(r, rc + Math.sqrt(Q * Q - d * d)); }
    return r;
  };
  const oben = (r) => {
    let h = -Infinity;
    for (const [hc, rc] of mitten) {
      if (r <= rc) h = Math.max(h, hc + Q);
      else if (r - rc <= Q) h = Math.max(h, hc + Math.sqrt(Q * Q - (r - rc) ** 2));
    }
    return h;
  };
  /* Mantel als Funktion der Höhe, mit Schließung (Kehlen füllen):
     erst nach außen aufweiten, dann um ebenso viel zurück */
  const k = Math.round(kehle / S), hs = [];
  for (let j = -k; unten + j * S < H + dicke; j++) hs.push(unten + j * S);
  const r0 = hs.map(aussen);
  const kreis = (j) => Math.sqrt(Math.max(0, kehle * kehle - (j * S) ** 2));
  const weit = r0.map((_, i) => { let m = -Infinity; for (let j = -k; j <= k; j++) if (r0[i + j] > -Infinity) m = Math.max(m, r0[i + j] + kreis(j)); return m; });
  const zu = r0.map((r, i) => {
    if (!(r > -Infinity) || hs[i] > H - rho) return r;
    let m = Infinity; for (let j = -k; j <= k; j++) if (weit[i + j] > -Infinity) m = Math.min(m, weit[i + j] - kreis(j));
    return Math.max(r, m);
  });
  const mantel = [];
  for (let i = k; i < hs.length; i++) {
    mantel.push({ h: hs[i], r: zu[i] });
    if (i + 1 >= hs.length || (hs[i] > H - Q - 1 && !(zu[i + 1] > zu[i] - S))) break;
  }
  const ende = mantel[mantel.length - 1];
  const kuppel = form === FLASCHENFORMEN.champagner ? 9 : 0;
  const deckel = [ende];
  for (let r = Math.ceil(ende.r / S - 1) * S; r > 0; r -= S) deckel.push({ h: Math.max(ende.h, oben(r)), r });
  deckel.push({ h: oben(0), r: 0 });
  if (kuppel) {
    const r1 = deckel.find((p) => p.h >= H + dicke - 1e-6)?.r ?? ende.r;
    for (const p of deckel) if (p.r < r1) p.h += kuppel * Math.cos(Math.PI / 2 * p.r / r1) ** 2;
  }
  return { mantel, deckel };
}


/** Die Teile des Modells: Glaskörper (mit Boden), Kapselmantel und
    Kapseldeckel. `kapselAnteil` ist der Anteil der Höhe von oben. */
function flaschenTeile(form, { kapselAnteil = form.kapsel, art = "folie", segmente = 72 } = {}) {
  const H = form.hoehe;
  const teile = {};

  /* Glas: Boden als Scheibe zur Achse, dann die Umrisslinie. Unter
     einer Kapsel reicht es noch 10 mm hinein — es schaut sonst an der
     Kapselkante ein Spalt durch —, aber nicht bis oben: Die gerundete
     Kante der Kapsel liegt innerhalb der Ecke der Mündung. */
  const mitKapsel = art !== "ohne" && kapselAnteil > 0;
  const unten = Math.max(kapselTiefste(form), H * (1 - Math.min(0.5, kapselAnteil)));
  const glas = [{ h: 0.8, r: 0 }, ...profilPunkte(form, 0, mitKapsel ? Math.min(H, unten + 10) : H)];
  teile.glas = drehkoerperNetz(glas, { segmente, hoehe: H });

  if (mitKapsel) {
    const umriss = kapselUmriss(form, art, unten);
    /* Wachs läuft ungleichmäßig herab: wenige weiche Nasen statt
       eines Sägeblatts. Fest gewählte Phasen, damit dieselbe Flasche
       jedes Mal gleich aussieht; weich begrenzt (statt max(0, …)), damit
       die Nasen ohne Knick aus der Kante wachsen. */
    const weich = (x) => 0.6 * Math.log1p(Math.exp(x / 0.6));
    const unterkante = art === "wachs"
      ? (w) => -(weich(3.2 * Math.sin(w * 3 + 0.7) + 2.1 * Math.sin(w * 7 + 2.1) - 1.2))
      : null;
    teile.kapsel = drehkoerperNetz(umriss.mantel, { segmente, hoehe: H, unterkante });
    /* Deckel: über die gerundete Kante bis zur Achse, beim Champagner
       eine Kuppel über dem Agraffenkorken (siehe kapselUmriss) */
    const deckel = umriss.deckel;
    teile.deckel = drehkoerperNetz(deckel, { segmente, hoehe: H });
  }
  return teile;
}


export {
  FLASCHENFORMEN, FORM_REIHE, KAPSEL_ARTEN, KAPSEL_DICKE,
  drehkoerperNetz, flaschenTeile, formVon, halsBeginn, kapselArt, kapselTiefste, kapselUmriss, koerperRadius,
  profilPunkte, radiusBei, vorschlagForm,
};
